/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { Space } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ModelBankBalanceDetailedSummary } from 'account-client'
import { PlatformAnchor } from '@/components/PlatformAnchor'
import { PlatformList } from '@/components/PlatformList'
import { TPlatformListService } from '@/components/PlatformList/interface'
import { useEnumFilter } from '@/hooks/useEnumFilter'
import { useTimeRangeFilter } from '@/hooks/useTimeRangeFilter'
import { useTranslateAmount } from '@/hooks/useTranslateAmount'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { useCashDetailsColumns } from '@/pages/billing/IncomeExpense/CashDetails/useCashDetailsColumns'
import { BankBalanceDetailType } from '@/pages/billing/interface'
import { accountClient } from '@/services/cloudClient/accountClient'
import { createOrders } from '@/utils/createOrders'

export const CASH_DETAILS = 'cash_details'

export function CashDetails() {
  const { t } = useTranslation()
  const { contractId } = useParams()
  const { translateAmount } = useTranslateAmount()
  const { filter: typeFilter, value: type } = useEnumFilter<BankBalanceDetailType>(BankBalanceDetailType, t('All Types'))
  const { filter: timeRangeFilter, timeRange } = useTimeRangeFilter(null, { allowClear: true })
  const [bankBalanceDetails, setBankBalanceDetails] = useState<ModelBankBalanceDetailedSummary>()
  const columns = useCashDetailsColumns()
  const emitter = PlatformList.useCreateEmitter()

  const service: TPlatformListService = ({ pagination, sorter }, options) => {
    const { current, pageSize } = pagination || {}
    return accountClient.bankBalanceApi.listBankBalanceDetails(
      contractId,
      {
        pageNum: current,
        pageSize,
        start: timeRange?.[0]?.valueOf(),
        end: timeRange?.[1]?.valueOf(),
        types: type ? [type] : undefined,
        ...createOrders(sorter),
      },
      options,
    )
  }

  useEffect(() => {
    accountClient.bankBalanceApi
      .getBankBalanceDetailedSummary(contractId, {
        start: timeRange?.[0]?.valueOf(),
        end: timeRange?.[1]?.valueOf(),
        types: type ? [type] : undefined,
      })
      .then(res => setBankBalanceDetails(res.data))
    emitter.fire({
      pagination: {
        current: 1,
        pageSize: 10,
      },
    })
  }, [type, timeRange])

  return (
    <LayoutOthers.ContentCard title={<PlatformAnchor id={CASH_DETAILS}>{t('Cash Details')}</PlatformAnchor>} isTable>
      <PlatformList
        emitter={emitter}
        service={service}
        columns={columns}
        rowKey={record => record.gmtTime + record.type}
        createTitle={() => (
          <>
            <Space>
              {typeFilter}
              {timeRangeFilter}
            </Space>
            <Space size="large">
              <span>
                {t('Total Deposit Amount')}: {translateAmount(bankBalanceDetails?.depositAmountFormatted)}
              </span>
              <span>
                {t('Total Deduct Amount')}: {translateAmount(bankBalanceDetails?.deductAmountFormatted)}
              </span>
              <span>
                {t('Total Withdraw Amount')}: {translateAmount(bankBalanceDetails?.withdrawAmountFormatted)}
              </span>
            </Space>
          </>
        )}
      />
    </LayoutOthers.ContentCard>
  )
}
